import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, FileText, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apps, setApps] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [creating, setCreating] = useState(false);

  const firstName = (() => {
    const w = (user?.name || "").trim().split(/\s+/)[0];
    return w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : "";
  })();

  const load = async () => {
    try {
      const [a, c] = await Promise.all([api.get("/applications"), api.get("/campaigns/active").catch(() => ({ data: null }))]);
      setApps(a.data);
      setCampaign(c.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setApps([]);
    }
  };
  useEffect(() => { load(); }, []);

  const startApplication = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/applications", {});
      navigate(`/solicitud/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const openApp = (app) => {
    if (app.status === "draft") navigate(`/solicitud/${app.id}`);
    else if (app.status === "additional_info_required") navigate(`/solicitud/${app.id}/informacion`);
    else navigate(`/solicitud/${app.id}/resultado`);
  };

  return (
    <AppShell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" data-testid="dashboard-greeting">{firstName ? `Hola, ${firstName}` : "Mis solicitudes"}</h1>
        <p className="text-muted-foreground mt-1">
          {firstName ? "Estas son tus solicitudes" : "Bienvenido"}
          {campaign && <> · Campaña vigente: <strong>{campaign.name}</strong></>}
        </p>
      </div>

      {apps === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : apps.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-10 text-center">
          <FileText className="w-12 h-12 text-accent mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Aún no tienes solicitudes</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Inicia tu solicitud de reducción de cuota. Guardamos tu avance automáticamente para que puedas continuar cuando quieras.
          </p>
          <Button onClick={startApplication} disabled={creating} className="btn-lift h-14 text-lg px-8 rounded-xl" data-testid="btn-new-application">
            {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-5 h-5 mr-2" /> Iniciar solicitud</>}
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {["current", "past"].map((group) => {
            const list = apps.filter((a) => group === "current" ? a.campaign_active : !a.campaign_active);
            if (list.length === 0) return null;
            return (
              <div key={group}>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  {group === "current" ? "Campaña actual" : "Años anteriores"}
                </h2>
                <div className="space-y-4">
                  {list.map((app) => (
                    <button key={app.id} onClick={() => openApp(app)}
                      className="w-full text-left bg-white rounded-2xl border border-border p-6 btn-lift flex items-center justify-between"
                      data-testid={`app-card-${app.id}`}>
                      <div>
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="font-heading font-bold text-lg text-primary">{app.human_id || "Borrador"}</span>
                          <StatusBadge status={app.public_status} testId={`app-status-${app.id}`} />
                          {app.campaign_name && <span className="text-xs bg-secondary text-primary px-2 py-1 rounded-full font-semibold">{app.campaign_name}</span>}
                        </div>
                        {app.status === "additional_info_required" && (
                          <p className="text-sm text-orange-700 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Necesitamos información adicional</p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          {app.status === "draft" ? "Continúa completando tu solicitud" : "Ver detalle"}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-primary" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <Button onClick={startApplication} disabled={creating} variant="outline"
            className="btn-lift h-12 rounded-xl border-2 border-primary text-primary bg-white" data-testid="btn-new-application">
            <Plus className="w-5 h-5 mr-2" /> Nueva solicitud
          </Button>
        </div>
      )}
    </AppShell>
  );
}
